import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { isGerman } from '@/i18n/locale';
import { Icon } from './icons';

// Full-viewport states shared by every guarded route: the auth gate, the
// baby-store load, a resumed chat that is still fetching, an error with a
// way out. One component so the app's "between screens" moment looks the
// same everywhere instead of six slightly different grey captions.

export function FullPageState({
  label,
  children,
  busy = true,
}: {
  label?: string;
  children?: ReactNode;
  busy?: boolean;
}) {
  const de = isGerman();
  const text = label ?? (de ? 'Lädt…' : 'Loading…');
  return (
    <div
      className="min-h-[100dvh] flex flex-col items-center justify-center bg-cream px-6 text-center"
      role={busy ? 'status' : undefined}
      aria-live={busy ? 'polite' : undefined}
    >
      <div
        className={
          'w-12 h-12 rounded-2xl bg-primary flex items-center justify-center shadow-sm ' +
          (busy ? 'animate-pulse' : '')
        }
        aria-hidden
      >
        <span className="text-white text-xl font-display font-semibold">A</span>
      </div>
      <p className="mt-4 text-sm text-slate-muted max-w-xs">{text}</p>
      {children}
    </div>
  );
}

// Header back control. Same arrow glyph, hit area and hover surface on every
// screen; the visible label is optional (headers with a title next to it
// usually omit it, standalone legal pages show it).
export function BackLink({
  to,
  label,
  showLabel = false,
  className = '',
}: {
  to: string;
  label?: string;
  showLabel?: boolean;
  className?: string;
}) {
  const de = isGerman();
  const text = label ?? (de ? 'Zurück' : 'Back');
  return (
    <Link
      to={to}
      aria-label={showLabel ? undefined : text}
      className={
        (showLabel
          ? 'inline-flex items-center gap-1.5 rounded-xl px-2 py-2 -ml-2 text-sm font-medium text-slate-muted hover:text-slate hover:bg-primary-50 active:bg-primary-100 transition-colors '
          : 'icon-btn -ml-2 ') + className
      }
    >
      <Icon.ArrowLeft size={20} />
      {showLabel && <span>{text}</span>}
    </Link>
  );
}
